import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StatusBar, View } from 'react-native';
import Animated, {
  FadeInDown,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
} from 'react-native-reanimated';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Heading } from '@/common/components/Heading/Heading';
import { IconCoverflex } from '@/common/components/Icons/custom/IconCoverflex';
import { RouterLink } from '@/common/components/RouterLink/RouterLink';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { useKeyboard } from '@/common/hooks/useKeyboard';
import { SplitLayout } from '@/common/layouts/SplitLayout/SplitLayout';
import { tv } from '@/common/theme/tv.config';
import { colors } from '@coverflex-tech/hypernova-design-tokens';

export type TAuthLayoutProps = {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
};

export const AuthLayout = (props: TAuthLayoutProps) => {
  return (
    <SplitLayout.Root>
      <SplitLayout.Primary>{props.children}</SplitLayout.Primary>
      <SplitLayout.Secondary>
        <AuthSecondaryContent {...props} />
      </SplitLayout.Secondary>
    </SplitLayout.Root>
  );
};

const AuthSecondaryContent = ({
  title,
  description,
}: Partial<TAuthLayoutProps>) => {
  const { t } = useTranslation();
  const { isKeyboardVisible } = useKeyboard();
  const matches = useBreakpoints(['lg']);

  const translateX = useSharedValue<`${number}%`>('-100%');

  useEffect(() => {
    translateX.value = '-50%';
  }, [translateX]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: '-50%' },
      {
        translateX: withDelay(
          500,
          withSpring(translateX.value, {
            damping: 15,
            mass: 0.8,
            stiffness: 10,
          })
        ),
      },
    ],
  }));

  if (matches.lg) {
    return (
      <Stack align="center" horizontal className={rowStyles.rightPanel}>
        <Animated.View className={rowStyles.circle} style={animatedStyle} />
        <Stack gap="lg" className={rowStyles.rightContent}>
          <Heading level={1} color="neutral10" className={rowStyles.title}>
            {title ?? t('authentication.main_title')}
          </Heading>
          <BodyText color="neutral10" className={rowStyles.description}>
            {description ?? t('authentication.main_description')}
          </BodyText>
        </Stack>
      </Stack>
    );
  }

  return (
    <Animated.View
      className={columnStyles.header({
        keyboardVisible: isKeyboardVisible,
      })}
    >
      <View
        className={columnStyles.headerContent({
          keyboardVisible: isKeyboardVisible,
        })}
      >
        <Stack gap="2xl" align="center">
          <IconCoverflex color={colors.light.semantic.surface.neutral10} />
          <Stack gap="xs" align="center">
            <Heading level={1} color="neutral10">
              {title ?? t('authentication.main_title')}
            </Heading>
            <BodyText color="neutral10" className="text-center">
              {description ?? t('authentication.main_description')}
            </BodyText>
          </Stack>
        </Stack>
        <Animated.View
          className={columnStyles.cicle({
            keyboardVisible: isKeyboardVisible,
          })}
          entering={FadeInDown.duration(1000)}
        />
      </View>
    </Animated.View>
  );
};

export type TAuthFormLayoutProps = {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
};

export function AuthFormLayout(props: TAuthFormLayoutProps) {
  const matches = useBreakpoints(['lg']);
  const { isKeyboardVisible } = useKeyboard();

  if (matches.lg) {
    return (
      <AuthLayout {...props}>
        <Stack gap="lg" className={rowStyles.leftContent}>
          <Stack gap="xl">
            <RouterLink href={ROUTES.SIGNIN} className={rowStyles.logoLink}>
              <IconCoverflex />
            </RouterLink>
            {props.children}
          </Stack>
          <Stack className={rowStyles.footer} gap="xl">
            {props.footer}
          </Stack>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout {...props}>
      <StatusBar
        backgroundColor={
          isKeyboardVisible
            ? colors.light.semantic.surface.neutral20
            : colors.light.semantic.surface.accentSecondary40
        }
      />
      <ScrollView
        className={columnStyles.formContainer({
          keyboardVisible: isKeyboardVisible,
        })}
      >
        {props.children}
      </ScrollView>
      <View className={columnStyles.footer}>{props.footer}</View>
    </AuthLayout>
  );
}

export const columnStyles = {
  header: tv({
    base: 'pt-safe grow bg-accentSecondary40',
    variants: {
      keyboardVisible: {
        true: 'grow-0',
      },
    },
  }),
  headerContent: tv({
    base: 'relative min-h-[25.6rem] grow items-center justify-center overflow-hidden bg-accentSecondary40 px-lg pb-2xl',
    variants: {
      keyboardVisible: {
        true: 'mt-sm h-0 min-h-0 grow-0',
      },
    },
  }),
  cicle: tv({
    base: 'absolute -bottom-[6rem] mt-auto h-[12rem] w-[12rem] rounded-full bg-accentSecondary30',
    variants: {
      keyboardVisible: {
        true: 'hidden',
      },
    },
  }),
  formContainer: tv({
    base: 'grow-0 p-lg',
    variants: {
      keyboardVisible: {
        true: 'pt-safe grow',
      },
    },
  }),
  footer: 'border-t border-neutral20 px-lg py-md bg-neutral20',
};

export const rowStyles = {
  leftPanel: ' bg-neutral20 h-full w-full',
  leftContent:
    'w-full max-w-[64rem] p-2xl mx-auto flex flex-col justify-center h-full',
  rightPanel: 'relative px-2xl overflow-hidden bg-accentSecondary40 h-full',
  rightContent: 'px-[10vw] pl-[12vw]',
  circle:
    'absolute top-1/2 left-0 z-2 w-[23vw] h-[23vw] bg-accentSecondary30 rounded-full',
  logoLink: 'self-start',
  title: 'max-w-[35rem]',
  description: 'max-w-[35rem]',
  footer: 'items-start',
};
